import React, { useEffect, useState } from 'react';

interface UseDynamicLimitProps {
  fixedLimit?: number;
  items: any[];
  reservedSpaceForLastElement?: number;
  horizontalGapBetweenItems?: number;
  overlapBetweenItems?: number;
  additionalPaddingPerItem?: number;
  parentElement: HTMLElement | null;
}

export const useDynamicVisibleItemsLimit = (
  containerRef: React.RefObject<HTMLElement | null>,
  {
    fixedLimit,
    items,
    reservedSpaceForLastElement = 30,
    horizontalGapBetweenItems = 16,
    overlapBetweenItems,
    additionalPaddingPerItem = 0,
    parentElement,
  }: UseDynamicLimitProps
) => {
  const [visibleItemsLimit, setVisibleItemsLimit] = useState(items.length);

  useEffect(() => {
    if (fixedLimit !== undefined || !containerRef || !parentElement) {
      setVisibleItemsLimit(fixedLimit || 2);
      return () => {};
    }

    const calculateVisibleItemsLimit = () => {
      if (!parentElement || !containerRef.current) {
        return;
      }

      const parentElementWidth = parentElement.offsetWidth;
      const spaceAvailableForItems = parentElementWidth - reservedSpaceForLastElement;
      const childElements = Array.from(containerRef.current?.children) as HTMLElement[];

      const itemElements = childElements.filter(child => child.tagName !== 'BUTTON');

      const { countOfVisibleElements } = itemElements.reduce(
        (accumulator, currentItem, index) => {
          if (accumulator.limitReached) return accumulator;

          const widthOfCurrentItem = currentItem.offsetWidth + additionalPaddingPerItem;
          const widthAfterAddingCurrentItem = overlapBetweenItems
            ? index === 0
              ? widthOfCurrentItem
              : accumulator.currentTotalWidth + widthOfCurrentItem - overlapBetweenItems
            : accumulator.currentTotalWidth + widthOfCurrentItem + (index > 0 ? horizontalGapBetweenItems : 0);

          if (widthAfterAddingCurrentItem <= spaceAvailableForItems) {
            accumulator.currentTotalWidth = widthAfterAddingCurrentItem;
            accumulator.countOfVisibleElements++;
          } else {
            accumulator.limitReached = true;
          }
          return accumulator;
        },
        { currentTotalWidth: 0, countOfVisibleElements: 0, limitReached: false }
      );

      setVisibleItemsLimit(countOfVisibleElements);
    };

    calculateVisibleItemsLimit();

    const resizeObserver = new ResizeObserver(() => calculateVisibleItemsLimit());
    resizeObserver.observe(parentElement);

    window.addEventListener('resize', calculateVisibleItemsLimit);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateVisibleItemsLimit);
    };
  }, [items.length, fixedLimit, containerRef, parentElement, overlapBetweenItems, horizontalGapBetweenItems, reservedSpaceForLastElement]);

  return fixedLimit ?? visibleItemsLimit;
};
